import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, X, LogOut, RefreshCw, Image as ImageIcon, History, CheckSquare, Square, Search, Filter, Undo2, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";

const UNDO_WINDOW_MS = 10_000;
const PAGE_SIZE = 24;

const reviewsSearchSchema = z.object({
  status: fallback(z.enum(["pending", "approved", "rejected", "all"]), "pending").default("pending"),
  from: fallback(z.string(), "").default(""),
  to: fallback(z.string(), "").default(""),
  q: fallback(z.string(), "").default(""),
  sort: fallback(z.enum(["newest", "oldest"]), "newest").default("newest"),
  page: fallback(z.number().int().min(1), 1).default(1),
});

export const Route = createFileRoute("/_authenticated/admin/reviews")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "reviewSubmissions")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  validateSearch: zodValidator(reviewsSearchSchema),
  head: () => ({
    meta: [
      { title: "Photo Review Queue — Coffman Cove Lodging" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReviewsQueue,
});

type Submission = {
  id: string;
  submitter_name: string;
  submitter_email: string | null;
  caption: string | null;
  storage_path: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  reviewer_notes: string | null;
};

type Row = Submission & { previewUrl: string | null };

type AuditEntry = {
  id: string;
  submission_id: string;
  action: "approved" | "rejected" | "pending";
  reviewer_id: string | null;
  reviewer_email: string | null;
  created_at: string;
};

type ActivityEntry = {
  id: string;
  actor_id: string | null;
  actor_email: string | null;
  action: "bulk_approve" | "bulk_reject" | "undo_bulk";
  submission_count: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

function ReviewsQueue() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  type UndoState = {
    action: "approved" | "rejected";
    items: { id: string; prevStatus: "pending" | "approved" | "rejected" }[];
    expiresAt: number;
  };
  const [undoState, setUndoState] = useState<UndoState | null>(null);
  const [undoNow, setUndoNow] = useState(Date.now());
  const [undoBusy, setUndoBusy] = useState(false);

  type StatusFilter = "pending" | "approved" | "rejected" | "all";
  type SortOrder = "newest" | "oldest";
  const { status: statusFilter, from: dateFrom, to: dateTo, q: searchEmail, sort: sortOrder, page } = Route.useSearch();
  const updateFilters = useCallback(
    (patch: Partial<{ status: StatusFilter; from: string; to: string; q: string; sort: SortOrder; page: number }>) => {
      navigate({
        to: "/admin/reviews",
        search: (prev: Record<string, unknown>) => {
          // Any filter/sort change resets pagination to page 1, unless page is
          // being set explicitly in the same patch.
          const resetsPage =
            ("status" in patch || "from" in patch || "to" in patch || "q" in patch || "sort" in patch) &&
            !("page" in patch);
          return { ...prev, ...patch, ...(resetsPage ? { page: 1 } : {}) };
        },
        replace: true,
      });
    },
    [navigate],
  );
  const setStatusFilter = (v: StatusFilter) => updateFilters({ status: v });
  const setDateFrom = (v: string) => updateFilters({ from: v });
  const setDateTo = (v: string) => updateFilters({ to: v });
  const setSearchEmail = (v: string) => updateFilters({ q: v });
  const setSortOrder = (v: SortOrder) => updateFilters({ sort: v });
  const setPage = (v: number) => updateFilters({ page: v });

  const load = useCallback(async () => {

    setLoading(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      setUserEmail(userRes.user?.email ?? null);

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userRes.user?.id ?? "");
      const admin = !!roles?.some((r) => r.role === "admin");
      setIsAdmin(admin);
      if (!admin) {
        setRows([]);
        return;
      }

      const fromIdx = (page - 1) * PAGE_SIZE;
      const toIdx = fromIdx + PAGE_SIZE - 1;
      let query = supabase
        .from("gallery_submissions")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: sortOrder === "oldest" })
        .range(fromIdx, toIdx);
      if (statusFilter !== "all") query = query.eq("status", statusFilter);
      if (dateFrom) query = query.gte("created_at", `${dateFrom}T00:00:00`);
      if (dateTo) query = query.lte("created_at", `${dateTo}T23:59:59`);
      const trimmedEmail = searchEmail.trim();
      if (trimmedEmail) query = query.ilike("submitter_email", `%${trimmedEmail}%`);
      const { data, error, count } = await query;
      if (error) throw error;
      setTotalCount(count ?? 0);


      const withUrls: Row[] = await Promise.all(
        (data ?? []).map(async (s) => {
          const { data: signed } = await supabase.storage
            .from("gallery-submissions")
            .createSignedUrl(s.storage_path, 60 * 60);
          return { ...(s as Submission), previewUrl: signed?.signedUrl ?? null };
        }),
      );
      setRows(withUrls);
      setSelected(new Set());


      const { data: auditData, error: auditErr } = await supabase
        .from("submission_review_audit")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (auditErr) throw auditErr;
      setAudit((auditData ?? []) as AuditEntry[]);

      const { data: activityData, error: activityErr } = await supabase
        .from("admin_activity_log")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (activityErr) throw activityErr;
      setActivity((activityData ?? []) as ActivityEntry[]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load submissions";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo, searchEmail, sortOrder, page]);

  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 250);
    return () => clearTimeout(t);
  }, [load]);


  async function refreshAudit() {
    const { data: auditData } = await supabase
      .from("submission_review_audit")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (auditData) setAudit(auditData as AuditEntry[]);
  }

  async function refreshActivity() {
    const { data } = await supabase
      .from("admin_activity_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (data) setActivity(data as ActivityEntry[]);
  }

  async function logActivity(
    action: "bulk_approve" | "bulk_reject" | "undo_bulk",
    submissionIds: string[],
    metadata: Record<string, unknown> = {},
  ) {
    const { data: userRes } = await supabase.auth.getUser();
    const uid = userRes.user?.id;
    if (!uid) return;
    const { error } = await supabase.from("admin_activity_log").insert({
      actor_id: uid,
      actor_email: userRes.user?.email ?? null,
      action,
      submission_ids: submissionIds,
      submission_count: submissionIds.length,
      metadata: metadata as never,
    });
    if (error) {
      // Non-fatal — surface but don't block the action.
      console.warn("activity log insert failed", error.message);
    }
  }


  async function decide(id: string, status: "approved" | "rejected") {
    const prev = rows;
    setRows((r) => r?.filter((x) => x.id !== id) ?? r);
    setSelected((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    const { error } = await supabase
      .from("gallery_submissions")
      .update({ status })
      .eq("id", id);
    if (error) {
      setRows(prev);
      toast.error(error.message);
      return;
    }
    toast.success(status === "approved" ? "Approved" : "Rejected");
    await refreshAudit();
  }

  const [confirmAction, setConfirmAction] = useState<"approved" | "rejected" | null>(null);

  function requestBulkDecide(status: "approved" | "rejected") {
    if (selected.size === 0) return;
    if (undoState) {
      toast.info("Finish or dismiss the pending undo before starting another bulk action.");
      return;
    }
    setConfirmAction(status);
  }

  async function bulkDecide(status: "approved" | "rejected") {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    const prev = rows;
    const snapshot = (prev ?? [])
      .filter((r) => selected.has(r.id))
      .map((r) => ({ id: r.id, prevStatus: r.status }));
    setRows((r) => r?.filter((x) => !selected.has(x.id)) ?? r);
    const { error } = await supabase
      .from("gallery_submissions")
      .update({ status })
      .in("id", ids);
    if (error) {
      setRows(prev);
      toast.error(error.message);
      setBulkBusy(false);
      return;
    }
    setSelected(new Set());
    toast.success(`${ids.length} ${status === "approved" ? "approved" : "rejected"}`);
    await refreshAudit();
    await logActivity(status === "approved" ? "bulk_approve" : "bulk_reject", ids, {
      prev_statuses: snapshot.reduce<Record<string, number>>((acc, s) => {
        acc[s.prevStatus] = (acc[s.prevStatus] ?? 0) + 1;
        return acc;
      }, {}),
    });
    await refreshActivity();
    setUndoState({ action: status, items: snapshot, expiresAt: Date.now() + UNDO_WINDOW_MS });
    setBulkBusy(false);
  }


  useEffect(() => {
    if (!undoState) return;
    const tick = setInterval(() => {
      const now = Date.now();
      setUndoNow(now);
      if (now >= undoState.expiresAt) {
        setUndoState(null);
      }
    }, 250);
    return () => clearInterval(tick);
  }, [undoState]);

  async function performUndo() {
    if (!undoState || undoBusy) return;
    setUndoBusy(true);
    try {
      const byStatus = new Map<string, string[]>();
      for (const it of undoState.items) {
        const arr = byStatus.get(it.prevStatus) ?? [];
        arr.push(it.id);
        byStatus.set(it.prevStatus, arr);
      }
      for (const [prevStatus, ids] of byStatus) {
        const { error } = await supabase
          .from("gallery_submissions")
          .update({ status: prevStatus as "pending" | "approved" | "rejected" })
          .in("id", ids);
        if (error) throw error;
      }
      toast.success(`Reverted ${undoState.items.length} submission${undoState.items.length === 1 ? "" : "s"}`);
      await logActivity(
        "undo_bulk",
        undoState.items.map((i) => i.id),
        { of_action: undoState.action },
      );
      setUndoState(null);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Undo failed";
      toast.error(msg);
    } finally {
      setUndoBusy(false);
    }
  }

  function toggleOne(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  function toggleAll() {
    if (!rows) return;
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }


  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [noteSaving, setNoteSaving] = useState<Record<string, boolean>>({});

  function noteValue(r: Row) {
    return noteDrafts[r.id] ?? r.reviewer_notes ?? "";
  }
  function noteDirty(r: Row) {
    return noteDrafts[r.id] !== undefined && noteDrafts[r.id] !== (r.reviewer_notes ?? "");
  }
  async function saveNote(id: string) {
    const draft = noteDrafts[id];
    if (draft === undefined) return;
    const trimmed = draft.trim().slice(0, 2000);
    setNoteSaving((s) => ({ ...s, [id]: true }));
    const { error } = await supabase
      .from("gallery_submissions")
      .update({ reviewer_notes: trimmed === "" ? null : trimmed })
      .eq("id", id);
    setNoteSaving((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((rs) =>
      rs?.map((r) => (r.id === id ? { ...r, reviewer_notes: trimmed === "" ? null : trimmed } : r)) ?? rs,
    );
    setNoteDrafts((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });
    toast.success("Note saved");
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  }


  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl">Photo Review Queue</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pending gallery submissions{userEmail ? ` · signed in as ${userEmail}` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={signOut}
            className="inline-flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      {isAdmin === false && (
        <div className="mt-10 rounded-lg border border-border bg-[var(--color-parchment)] p-6 text-sm">
          Your account is signed in but does not have admin access. Contact the site owner
          to be added.
        </div>
      )}

      {isAdmin && (
        <div className="mt-8">
          <div className="mb-4 rounded-lg border border-border bg-card p-3 sm:p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <Filter className="h-3.5 w-3.5" /> Filters
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium text-muted-foreground">Status</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="all">All statuses</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium text-muted-foreground">From date</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium text-muted-foreground">To date</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium text-muted-foreground">Submitter email</span>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    type="search"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-sm"
                  />
                </div>
              </label>
              <label className="flex flex-col gap-1 text-xs">
                <span className="font-medium text-muted-foreground inline-flex items-center gap-1">
                  <ArrowUpDown className="h-3 w-3" /> Sort by
                </span>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                  className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
              </label>
            </div>
            {(statusFilter !== "pending" || dateFrom || dateTo || searchEmail || sortOrder !== "newest") && (
              <button
                onClick={() =>
                  updateFilters({ status: "pending", from: "", to: "", q: "", sort: "newest" })
                }
                className="mt-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-ember)] hover:underline"
              >
                Reset filters
              </button>
            )}
          </div>


          {loading && !rows?.length && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {!loading && rows && rows.length === 0 && (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              <ImageIcon className="mx-auto h-8 w-8 mb-2 opacity-60" />
              No submissions match these filters.
            </div>
          )}

          {rows && rows.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-[var(--color-parchment)] px-3 py-2 text-sm">
              <button
                onClick={toggleAll}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 hover:bg-accent"
              >
                {selected.size === rows.length ? (
                  <CheckSquare className="h-4 w-4" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                {selected.size === rows.length ? "Clear all" : "Select all"}
              </button>
              <span className="text-muted-foreground">
                {selected.size} of {rows.length} selected
              </span>
              <div className="ml-auto flex items-center gap-2">
                {undoState && (
                  <span className="text-[11px] text-amber-800 hidden sm:inline">
                    Undo pending — finish or dismiss it first
                  </span>
                )}
                <button
                  disabled={selected.size === 0 || bulkBusy || !!undoState}
                  onClick={() => requestBulkDecide("approved")}
                  title={undoState ? "Finish or dismiss the pending undo first" : undefined}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check className="h-4 w-4" /> Approve selected
                </button>
                <button
                  disabled={selected.size === 0 || bulkBusy || !!undoState}
                  onClick={() => requestBulkDecide("rejected")}
                  title={undoState ? "Finish or dismiss the pending undo first" : undefined}
                  className="inline-flex items-center gap-1 rounded-md bg-red-600 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <X className="h-4 w-4" /> Reject selected
                </button>
              </div>
            </div>
          )}
          {undoState && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span className="font-medium">
                {undoState.items.length} submission{undoState.items.length === 1 ? "" : "s"}{" "}
                {undoState.action === "approved" ? "approved" : "rejected"}.
              </span>
              <span className="text-xs text-amber-800">
                Undo available for {Math.max(0, Math.ceil((undoState.expiresAt - undoNow) / 1000))}s
              </span>
              <button
                onClick={performUndo}
                disabled={undoBusy}
                className="ml-auto inline-flex items-center gap-1 rounded-md bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-amber-700 disabled:opacity-40"
              >
                <Undo2 className="h-4 w-4" /> {undoBusy ? "Reverting…" : "Undo"}
              </button>
              <button
                onClick={() => setUndoState(null)}
                className="text-xs uppercase tracking-wider hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rows?.map((s) => (
              <article
                key={s.id}
                className={
                  "rounded-lg border bg-white overflow-hidden shadow-sm flex flex-col transition " +
                  (selected.has(s.id)
                    ? "border-emerald-500 ring-2 ring-emerald-500/40"
                    : "border-border")
                }
              >
                <div className="relative aspect-[4/3] bg-muted">
                  <label className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-md bg-white/90 backdrop-blur px-2 py-1 text-xs font-medium shadow cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.has(s.id)}
                      onChange={() => toggleOne(s.id)}
                      className="h-4 w-4 accent-emerald-600"
                    />
                    Select
                  </label>
                  {s.previewUrl ? (
                    <img
                      src={s.previewUrl}
                      alt={s.caption ?? `Submission by ${s.submitter_name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                      Preview unavailable
                    </div>
                  )}
                </div>

                <div className="p-4 flex-1 flex flex-col gap-2 text-sm">
                  <div>
                    <p className="font-semibold">{s.submitter_name}</p>
                    {s.submitter_email && (
                      <p className="text-xs text-muted-foreground break-all">{s.submitter_email}</p>
                    )}
                  </div>
                  {s.caption && <p className="text-muted-foreground">{s.caption}</p>}
                  <p className="mt-auto text-[11px] uppercase tracking-wider text-muted-foreground">
                    {new Date(s.created_at).toLocaleString()}
                  </p>
                  {s.status === "pending" ? (
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => decide(s.id, "approved")}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4" /> Approve
                      </button>
                      <button
                        onClick={() => decide(s.id, "rejected")}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-red-600 text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-red-700"
                      >
                        <X className="h-4 w-4" /> Reject
                      </button>
                    </div>
                  ) : (
                    <div className="pt-2">
                      <span
                        className={
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider " +
                          (s.status === "approved"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-red-100 text-red-800")
                        }
                      >
                        {s.status === "approved" ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {s.status}
                      </span>
                    </div>
                  )}

                  <div className="pt-2 border-t border-border mt-1">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                      Reviewer notes
                    </label>
                    <textarea
                      value={noteValue(s)}
                      onChange={(e) =>
                        setNoteDrafts((d) => ({ ...d, [s.id]: e.target.value.slice(0, 2000) }))
                      }
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && noteDirty(s)) {
                          e.preventDefault();
                          void saveNote(s.id);
                        }
                      }}
                      rows={2}
                      maxLength={2000}
                      placeholder="Internal notes (visible to admins only)"
                      className="w-full resize-y rounded-md border border-input bg-background px-2 py-1.5 text-xs"
                    />
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        {noteValue(s).length}/2000
                      </span>
                      <div className="flex gap-2">
                        {noteDirty(s) && (
                          <button
                            type="button"
                            onClick={() =>
                              setNoteDrafts((d) => {
                                const n = { ...d };
                                delete n[s.id];
                                return n;
                              })
                            }
                            className="text-[11px] uppercase tracking-wider text-muted-foreground hover:underline"
                          >
                            Cancel
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void saveNote(s.id)}
                          disabled={!noteDirty(s) || !!noteSaving[s.id]}
                          className="inline-flex items-center gap-1 rounded-md bg-slate-800 text-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {noteSaving[s.id] ? "Saving…" : "Save note"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

              </article>
            ))}
          </div>

          {totalCount > 0 && (
            <nav
              aria-label="Pagination"
              className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            >
              <span className="text-muted-foreground">
                {(() => {
                  const start = (page - 1) * PAGE_SIZE + 1;
                  const end = Math.min(page * PAGE_SIZE, totalCount);
                  return `Showing ${start}–${end} of ${totalCount}`;
                })()}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1 || loading}
                  className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <span className="text-xs text-muted-foreground">
                  Page {page} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page * PAGE_SIZE >= totalCount || loading}
                  className="inline-flex items-center gap-1 rounded-md border border-input px-3 py-1.5 text-xs font-semibold uppercase tracking-wider hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </nav>
          )}


          <section className="mt-14">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-display text-xl">Admin activity</h2>
              <span className="text-xs text-muted-foreground">
                ({activity.length} shown, bulk actions and undos)
              </span>
            </div>
            {activity.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                No bulk actions recorded yet.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">When</th>
                      <th className="text-left px-4 py-2 font-medium">Action</th>
                      <th className="text-left px-4 py-2 font-medium">Admin</th>
                      <th className="text-left px-4 py-2 font-medium">Count</th>
                      <th className="text-left px-4 py-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {activity.map((entry) => {
                      const label =
                        entry.action === "bulk_approve"
                          ? "Bulk approve"
                          : entry.action === "bulk_reject"
                            ? "Bulk reject"
                            : "Undo bulk";
                      const cls =
                        entry.action === "bulk_approve"
                          ? "bg-emerald-100 text-emerald-800"
                          : entry.action === "bulk_reject"
                            ? "bg-red-100 text-red-800"
                            : "bg-amber-100 text-amber-900";
                      const meta = entry.metadata ?? {};
                      let detail: string | null = null;
                      if (entry.action === "undo_bulk" && typeof meta.of_action === "string") {
                        detail = `undo of ${meta.of_action === "approved" ? "bulk approve" : "bulk reject"}`;
                      } else if (meta.prev_statuses && typeof meta.prev_statuses === "object") {
                        detail = Object.entries(meta.prev_statuses as Record<string, number>)
                          .map(([k, v]) => `${v} ${k}`)
                          .join(", ");
                      }
                      return (
                        <tr key={entry.id}>
                          <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                            {new Date(entry.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={
                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider " +
                                cls
                              }
                            >
                              {entry.action === "undo_bulk" ? (
                                <Undo2 className="h-3 w-3" />
                              ) : entry.action === "bulk_approve" ? (
                                <Check className="h-3 w-3" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                              {label}
                            </span>
                          </td>
                          <td className="px-4 py-2 break-all">
                            {entry.actor_email ?? (
                              <span className="text-muted-foreground italic">unknown</span>
                            )}
                          </td>
                          <td className="px-4 py-2 tabular-nums">{entry.submission_count}</td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">
                            {detail ?? "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mt-14">

            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-display text-xl">Recent decisions</h2>
              <span className="text-xs text-muted-foreground">
                ({audit.length} shown, newest first)
              </span>
            </div>
            {audit.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                No approvals or rejections yet.
              </p>
            ) : (
              <div className="mt-4 overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">When</th>
                      <th className="text-left px-4 py-2 font-medium">Action</th>
                      <th className="text-left px-4 py-2 font-medium">Reviewer</th>
                      <th className="text-left px-4 py-2 font-medium">Submission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {audit.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                          {new Date(entry.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider " +
                              (entry.action === "approved"
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-red-100 text-red-800")
                            }
                          >
                            {entry.action === "approved" ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <X className="h-3 w-3" />
                            )}
                            {entry.action}
                          </span>
                        </td>
                        <td className="px-4 py-2 break-all">
                          {entry.reviewer_email ?? (
                            <span className="text-muted-foreground italic">unknown</span>
                          )}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                          {entry.submission_id.slice(0, 8)}…
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {confirmAction && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-confirm-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !bulkBusy && setConfirmAction(null)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white shadow-xl border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h2 id="bulk-confirm-title" className="font-display text-xl">
                {confirmAction === "approved" ? "Approve" : "Reject"} {selected.size} submission
                {selected.size === 1 ? "" : "s"}?
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                You’re about to{" "}
                <span className="font-semibold text-foreground">
                  {confirmAction === "approved" ? "approve" : "reject"}
                </span>{" "}
                <span className="font-semibold text-foreground">{selected.size}</span> selected
                submission{selected.size === 1 ? "" : "s"}. You’ll have {UNDO_WINDOW_MS / 1000}s to
                undo afterwards.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-border bg-muted/30 px-6 py-3">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={bulkBusy}
                className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const action = confirmAction;
                  setConfirmAction(null);
                  await bulkDecide(action);
                }}
                disabled={bulkBusy}
                className={
                  "inline-flex items-center gap-1 rounded-md text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider disabled:opacity-40 " +
                  (confirmAction === "approved"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-red-600 hover:bg-red-700")
                }
              >
                {confirmAction === "approved" ? (
                  <><Check className="h-4 w-4" /> Approve {selected.size}</>
                ) : (
                  <><X className="h-4 w-4" /> Reject {selected.size}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
